import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiBookOpen,
  FiCheckCircle,
  FiHeart,
} from "react-icons/fi";
import { fetchDevotionHistory } from "../../store/devotionSlice";
import { useTranslation } from "react-i18next";

// -------- Glass surfaces (same vocabulary as ReadingPlansHub) --------------

const glassPanel = {
  background:
    "linear-gradient(135deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.08) 55%, rgba(255,255,255,0.15) 100%)",
  backdropFilter: "blur(22px) saturate(108%)",
  WebkitBackdropFilter: "blur(22px) saturate(108%)",
  border: "1px solid rgba(255,255,255,0.45)",
  boxShadow:
    "0 18px 44px rgba(55,79,99,0.13), inset 0 1px 0 rgba(255,255,255,0.55)",
};
const innerTranslucent = {
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.55), rgba(255,255,255,0.35))",
  border: "1px solid rgba(255,255,255,0.45)",
};
const primaryPill =
  "inline-flex items-center gap-2 h-11 px-space-md rounded-full bg-accent-cta text-white text-label-md font-semibold shadow-[-3px_-3px_7px_var(--outer-shadow-light),4px_5px_10px_rgba(0,0,0,0.22)] hover:bg-accent-cta-hover active:shadow-[inset_2px_2px_5px_rgba(0,0,0,0.3),inset_-2px_-2px_5px_rgba(255,255,255,0.2)] transition-all";
const secondaryPill =
  "inline-flex items-center gap-2 h-11 px-space-md rounded-full bg-white/60 text-neu-primary text-label-md font-semibold border border-white/60 hover:bg-white/80 active:bg-white/40 transition-all";

// -------- Date helpers -----------------------------------------------------

const isoOf = (y, m, d) =>
  `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const todayISOLocal = () => {
  const d = new Date();
  return isoOf(d.getFullYear(), d.getMonth() + 1, d.getDate());
};
const shiftMonth = (year, month, delta) => {
  const total = year * 12 + (month - 1) + delta;
  return { year: Math.floor(total / 12), month: (total % 12) + 1 };
};
const monthLabel = (year, month) =>
  new Date(year, month - 1, 1).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
  });
const spellDate = (dateISO) =>
  dateISO
    ? new Date(`${dateISO}T00:00:00`).toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
      })
    : "";

// -------- Preview / summary helpers ---------------------------------------

const REFLECTION_FIELDS = ["stands_out", "god_is_showing", "response"];
const hasReflection = (entry) =>
  REFLECTION_FIELDS.some((f) => (entry?.[f] || "").trim());
const hasPrayer = (entry) => Boolean((entry?.prayer || "").trim());
const previewText = (entry) =>
  entry.stands_out || entry.god_is_showing || entry.response || entry.prayer || entry.diary || "";

// -------- Root component ---------------------------------------------------

const DevotionHistoryPage = () => {
  const dispatch = useDispatch();
  const { history } = useSelector((state) => state.devotions);
  const { i18n } = useTranslation();
  const [params, setParams] = useSearchParams();

  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("bibleMemosTheme") || "powder"; } catch (_e) { return "powder"; }
  });
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "bibleMemosTheme" && e.newValue) setTheme(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const [selectedDate, setSelectedDate] = useState(
    () => params.get("date") || todayISOLocal()
  );
  const [month, setMonth] = useState(() => {
    const [y, m] = (params.get("date") || todayISOLocal()).split("-").map(Number);
    return { year: y, month: m };
  });
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const PAGE_SIZE = 10;
  const [recentPage, setRecentPage] = useState(1);

  // Fetch recent history / search results.
  // When not searching, scope to the calendar's visible month so navigating
  // the calendar pages the reflections list through history too.
  useEffect(() => {
    const params = { q: debouncedQuery || "", from: "", to: "" };
    if (!debouncedQuery) {
      const lastDay = new Date(month.year, month.month, 0).getDate();
      params.from = isoOf(month.year, month.month, 1);
      params.to = isoOf(month.year, month.month, lastDay);
    }
    dispatch(fetchDevotionHistory(params));
  }, [dispatch, debouncedQuery, month]);

  // Fetch the calendar month.
  const [calendarDays, setCalendarDays] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState(null);
  const loadCalendar = useCallback(() => {
    setCalendarLoading(true);
    setCalendarError(null);
    const lang = i18n.language?.startsWith("ko") ? "ko" : "en";
    fetch(
      `/api/devotions/calendar?year=${month.year}&month=${month.month}&language=${lang}`,
      { credentials: "include" }
    )
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("Failed"))))
      .then((payload) => {
        setCalendarDays(payload?.days || []);
      })
      .catch((err) => setCalendarError(err.message))
      .finally(() => setCalendarLoading(false));
  }, [month, i18n.language]);
  useEffect(() => { loadCalendar(); }, [loadCalendar]);

  const daysMap = useMemo(() => {
    const map = new Map();
    for (const d of calendarDays) map.set(d.date, d);
    return map;
  }, [calendarDays]);

  // Fetch the selected day's payload (verses omitted for preview view).
  const [selectedDay, setSelectedDay] = useState({ loading: false, data: null, error: null });
  useEffect(() => {
    let cancelled = false;
    setSelectedDay({ loading: true, data: null, error: null });
    fetch(`/api/devotions/date/${selectedDate}?language=${i18n.language?.startsWith("ko") ? "ko" : "en"}`, {
      credentials: "include",
    })
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.error || "No devotion for this date");
        }
        return r.json();
      })
      .then((data) => { if (!cancelled) setSelectedDay({ loading: false, data, error: null }); })
      .catch((err) => { if (!cancelled) setSelectedDay({ loading: false, data: null, error: err.message }); });
    return () => { cancelled = true; };
  }, [selectedDate, i18n.language]);

  const pickDate = (dateISO) => {
    setSelectedDate(dateISO);
    const next = new URLSearchParams(params);
    next.set("date", dateISO);
    setParams(next, { replace: true });
    // Move the calendar month if the picked date is outside the visible month.
    const [y, m] = dateISO.split("-").map(Number);
    if (y !== month.year || m !== month.month) setMonth({ year: y, month: m });
  };

  const todayISO = todayISOLocal();

  // ------ Summary strip counts (from history items). ------
  const startOfWeek = useMemo(() => {
    const d = new Date();
    const dow = d.getDay();
    const daysSinceMon = (dow + 6) % 7;
    d.setDate(d.getDate() - daysSinceMon);
    return isoOf(d.getFullYear(), d.getMonth() + 1, d.getDate());
  }, []);
  const monthPrefix = `${month.year}-${String(month.month).padStart(2, "0")}`;

  // Filter + paginate the recent-reflections list.
  // When searching → show all search results. Otherwise → scope to the
  // calendar's visible month so navigating months filters the list too.
  const visibleReflections = useMemo(() => {
    const items = history?.items || [];
    if (debouncedQuery) return items;
    return items.filter((row) =>
      (row?.passage?.passage_date || "").startsWith(monthPrefix)
    );
  }, [history, debouncedQuery, monthPrefix]);

  const totalPages = Math.max(1, Math.ceil(visibleReflections.length / PAGE_SIZE));
  const safePage = Math.min(recentPage, totalPages);
  const pagedReflections = useMemo(
    () =>
      visibleReflections.slice(
        (safePage - 1) * PAGE_SIZE,
        safePage * PAGE_SIZE
      ),
    [visibleReflections, safePage]
  );

  // Reset to page 1 whenever the filter window changes.
  useEffect(() => {
    setRecentPage(1);
  }, [debouncedQuery, monthPrefix]);

  const summary = useMemo(() => {
    const items = history?.items || [];
    let monthCount = 0;
    let weekCount = 0;
    let sundayCount = 0;
    for (const row of items) {
      const dateISO = row?.passage?.passage_date;
      if (!dateISO) continue;
      if (dateISO.startsWith(monthPrefix)) monthCount += 1;
      if (dateISO >= startOfWeek && dateISO <= todayISO) weekCount += 1;
      if (row?.has_sunday_preparation) sundayCount += 1;
    }
    return { monthCount, weekCount, sundayCount };
  }, [history, monthPrefix, startOfWeek, todayISO]);

  // ---------- Rendering helpers ----------

  const renderSummaryStat = (shortLabel, fullLabel, value) => (
    <div
      className="flex flex-col items-center justify-center rounded-2xl px-2 sm:px-space-md py-2 text-center"
      style={innerTranslucent}
    >
      <span className="text-headline-md font-noto-serif text-neu-primary">{value}</span>
      <span className="text-[10px] sm:text-label-sm uppercase tracking-wider text-on-surface-variant leading-tight">
        <span className="sm:hidden">{shortLabel}</span>
        <span className="hidden sm:inline">{fullLabel}</span>
      </span>
    </div>
  );

  const goPrevMonth = () => setMonth((v) => shiftMonth(v.year, v.month, -1));
  const goNextMonth = () => setMonth((v) => shiftMonth(v.year, v.month, 1));
  const goToday = () => pickDate(todayISO);

  const firstWeekday = new Date(month.year, month.month - 1, 1).getDay();
  const daysInMonth = new Date(month.year, month.month, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7) cells.push(null);

  return (
    <div
      data-theme={theme}
      className="min-h-screen bg-page-bg text-on-surface font-inter transition-colors"
    >
      <div className="w-full">
        <div className="w-full max-w-[1200px] mx-auto px-margin-mobile md:px-margin-neu py-space-xl flex flex-col gap-space-lg">
          {/* ---- Header ---- */}
          <div className="flex flex-col gap-space-xs">
            <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
              Personal Journal
            </span>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-space-sm">
              <div>
                <h1 className="font-noto-serif text-headline-lg text-neu-primary">
                  My Devotions
                </h1>
                <p className="text-body-md text-on-surface-variant max-w-xl">
                  Return to what you noticed, prayed, and wrote.
                </p>
              </div>
              <label className="relative w-full md:w-80">
                <FiSearch
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant"
                  size={16}
                  aria-hidden="true"
                />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  aria-label="Search reflections"
                  placeholder="Search reflections, prayers, passages"
                  className="w-full pl-11 pr-4 h-11 rounded-full bg-white/60 border border-white/60 text-body-md text-neu-primary placeholder:text-on-surface-variant focus:outline-none focus:bg-white/80 transition-colors"
                />
              </label>
            </div>
          </div>

          {/* ---- Summary strip ---- */}
          <div className="grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-space-sm">
            {renderSummaryStat("Month", "This month", summary.monthCount)}
            {renderSummaryStat("Week", "This week", summary.weekCount)}
            {renderSummaryStat("Sunday", "Sunday preparation", summary.sundayCount)}
          </div>

          {/* ---- Calendar + selected day (2 cols on desktop) ---- */}
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-gutter lg:items-stretch">
            {/* Calendar */}
            <section
              className="rounded-[1.5rem] p-space-md md:p-space-lg flex flex-col gap-space-md"
              style={glassPanel}
              aria-label="Devotion calendar"
            >
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={goPrevMonth}
                  aria-label="Previous month"
                  className="w-10 h-10 rounded-full flex items-center justify-center text-neu-primary bg-white/50 border border-white/60 hover:bg-white/80 transition-all"
                >
                  <FiChevronLeft />
                </button>
                <h2 className="font-noto-serif text-headline-sm text-neu-primary">
                  {monthLabel(month.year, month.month)}
                </h2>
                <button
                  type="button"
                  onClick={goNextMonth}
                  aria-label="Next month"
                  className="w-10 h-10 rounded-full flex items-center justify-center text-neu-primary bg-white/50 border border-white/60 hover:bg-white/80 transition-all"
                >
                  <FiChevronRight />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 text-center text-label-sm font-semibold text-on-surface-variant">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((w) => (
                  <span key={w}>{w}</span>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {cells.map((d, idx) => {
                  if (!d) return <div key={`e${idx}`} className="min-h-[44px]" aria-hidden="true" />;
                  const dateISO = isoOf(month.year, month.month, d);
                  const dayData = daysMap.get(dateISO);
                  const status = dayData?.status; // 'complete' | 'started' | 'scheduled' | null
                  const isToday = dateISO === todayISO;
                  const isSelected = dateISO === selectedDate;
                  const hasSundayPrep = dayData?.has_sunday_preparation;
                  const spokenBits = [
                    isSelected ? "selected" : null,
                    status === "complete" ? "reflection saved" : null,
                    status === "started" ? "reflection started" : null,
                    hasSundayPrep ? "prepared for Sunday" : null,
                    isToday ? "today" : null,
                  ].filter(Boolean);
                  const ariaLabel =
                    `${spellDate(dateISO)}${spokenBits.length ? ", " + spokenBits.join(", ") : ""}`;
                  const filledClasses = isSelected
                    ? "bg-accent-cta text-white"
                    : isToday
                      ? "text-accent-cta border-2 border-accent-cta"
                      : "text-neu-primary bg-white/50 border border-white/50 hover:bg-white/70";
                  return (
                    <button
                      key={dateISO}
                      type="button"
                      onClick={() => pickDate(dateISO)}
                      aria-label={ariaLabel}
                      aria-pressed={isSelected}
                      aria-current={isToday ? "date" : undefined}
                      className={`min-h-[44px] flex flex-col items-center justify-center rounded-lg transition-all ${filledClasses}`}
                    >
                      <span className="text-label-md font-semibold">{d}</span>
                      <span className="flex items-center gap-0.5 mt-0.5">
                        {(status === "complete" || status === "started") && (
                          <span
                            aria-hidden="true"
                            className={`w-1.5 h-1.5 rounded-full ${
                              isSelected ? "bg-white" : "bg-[#3b82f6]"
                            }`}
                          />
                        )}
                        {hasSundayPrep && (
                          <span
                            aria-hidden="true"
                            className={`w-1.5 h-1.5 rounded-full ${
                              isSelected ? "bg-white" : "bg-accent-cta"
                            }`}
                          />
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Legend + jump-to-today */}
              <div className="flex items-center justify-between flex-wrap gap-2 text-label-sm">
                <ul className="flex items-center gap-3 text-on-surface-variant">
                  <li className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#3b82f6]" /> Reflection</li>
                  <li className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-accent-cta" /> Sunday</li>
                  <li className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded border-2 border-accent-cta" /> Today
                  </li>
                </ul>
                <button
                  type="button"
                  onClick={goToday}
                  className="text-label-sm text-accent-cta font-semibold hover:underline"
                >
                  Jump to today
                </button>
              </div>

              {calendarLoading && (
                <p className="text-label-sm text-on-surface-variant">Loading calendar…</p>
              )}
              {calendarError && (
                <p className="text-label-sm text-[#b02a37]">{calendarError}</p>
              )}
            </section>

            {/* Selected day preview */}
            <section
              className="rounded-[1.5rem] p-space-md md:p-space-lg flex flex-col gap-space-md"
              style={glassPanel}
              aria-live="polite"
            >
              <div className="flex flex-col gap-space-xs">
                <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
                  Selected day
                </span>
                <h2 className="font-noto-serif text-headline-md text-neu-primary">
                  {spellDate(selectedDate)}
                </h2>
              </div>

              {selectedDay.loading && (
                <p className="text-label-md text-on-surface-variant">Loading…</p>
              )}

              {!selectedDay.loading && selectedDay.data && (
                <SelectedDayPreview
                  data={selectedDay.data}
                  hasSundayPrep={daysMap.get(selectedDate)?.has_sunday_preparation}
                />
              )}

              {!selectedDay.loading && !selectedDay.data && (
                <div className="flex flex-col gap-space-sm">
                  <p className="text-body-md text-on-surface-variant">
                    No reflection was written for this day.
                  </p>
                  <div className="flex flex-wrap gap-space-sm">
                    <Link to={`/devotions/date/${selectedDate}`} className={primaryPill}>
                      <FiBookOpen />
                      <span>Read This Passage</span>
                    </Link>
                  </div>
                </div>
              )}
            </section>
          </div>

          {/* ---- Recent reflections ---- */}
          <section
            className="rounded-[1.5rem] p-space-md md:p-space-lg flex flex-col gap-space-md"
            style={glassPanel}
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex flex-col">
                <h2 className="font-noto-serif text-headline-sm text-neu-primary">
                  {debouncedQuery ? "Search results" : "Recent reflections"}
                </h2>
                {!debouncedQuery && (
                  <p className="text-label-sm text-on-surface-variant">
                    {monthLabel(month.year, month.month)} ·{" "}
                    {visibleReflections.length}{" "}
                    {visibleReflections.length === 1 ? "entry" : "entries"}
                  </p>
                )}
              </div>
              {debouncedQuery && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="text-label-sm text-accent-cta font-semibold hover:underline"
                >
                  Clear search
                </button>
              )}
            </div>

            {visibleReflections.length ? (
              <ul className="flex flex-col divide-y divide-white/40">
                {pagedReflections.map(({ entry, passage, has_sunday_preparation }) => {
                  const reflectionBadge = hasReflection(entry);
                  const prayerBadge = hasPrayer(entry);
                  return (
                    <li key={entry.id} className="py-space-sm flex flex-col gap-1">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <Link
                          to={`/devotions/date/${passage.passage_date}`}
                          className="font-noto-serif text-headline-sm text-neu-primary hover:text-accent-cta transition-colors"
                        >
                          {passage.passage_date} · {passage.reference_en || passage.reference_ko}
                        </Link>
                        <div className="flex flex-wrap gap-2 text-label-sm">
                          {entry.is_complete && (
                            <span
                              className="inline-flex items-center gap-1 px-space-sm py-1 rounded-full text-label-sm text-accent-cta font-semibold"
                              style={innerTranslucent}
                            >
                              <FiCheckCircle /> Complete
                            </span>
                          )}
                          {reflectionBadge && (
                            <span
                              className="inline-flex items-center gap-1 px-space-sm py-1 rounded-full text-label-sm text-neu-primary font-semibold"
                              style={innerTranslucent}
                            >
                              <FiBookOpen /> Reflection
                            </span>
                          )}
                          {prayerBadge && (
                            <span
                              className="inline-flex items-center gap-1 px-space-sm py-1 rounded-full text-label-sm text-neu-primary font-semibold"
                              style={innerTranslucent}
                            >
                              <FiHeart /> Prayer
                            </span>
                          )}
                          {has_sunday_preparation && (
                            <span
                              className="inline-flex items-center gap-1 px-space-sm py-1 rounded-full text-label-sm text-accent-cta font-semibold"
                              style={innerTranslucent}
                            >
                              Sunday
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="text-body-md text-on-surface-variant line-clamp-2">
                        {previewText(entry) || "No reflection text yet"}
                      </p>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="flex flex-col items-start gap-space-sm">
                <p className="text-body-md text-on-surface-variant">
                  {debouncedQuery
                    ? "No entries match your search."
                    : `No reflections for ${monthLabel(month.year, month.month)} yet.`}
                </p>
                {!debouncedQuery && (
                  <Link to="/devotions/today" className={secondaryPill}>
                    <FiBookOpen />
                    <span>Go to Today</span>
                  </Link>
                )}
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex items-center justify-between gap-space-sm pt-space-xs">
                <button
                  type="button"
                  onClick={() => setRecentPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="inline-flex items-center gap-1 h-9 px-space-sm rounded-full bg-white/60 border border-white/60 text-label-md text-neu-primary font-semibold hover:bg-white/80 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  aria-label="Previous page"
                >
                  <FiChevronLeft /> Prev
                </button>
                <span className="text-label-sm text-on-surface-variant">
                  Page {safePage} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setRecentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="inline-flex items-center gap-1 h-9 px-space-sm rounded-full bg-white/60 border border-white/60 text-label-md text-neu-primary font-semibold hover:bg-white/80 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  aria-label="Next page"
                >
                  Next <FiChevronRight />
                </button>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

// -------- Selected day preview ---------------------------------------------

const SelectedDayPreview = ({ data, hasSundayPrep }) => {
  const passage = data?.passage;
  const entry = data?.entry;
  const referenceEn = passage?.reference_en;
  const referenceKo = passage?.reference_ko;

  const reflectionExcerpt =
    entry?.stands_out || entry?.god_is_showing || entry?.response || "";
  const prayerExcerpt = entry?.prayer || "";

  return (
    <>
      <div className="flex flex-col gap-0.5">
        <h3 className="font-noto-serif text-headline-sm text-neu-primary">
          {referenceEn || referenceKo || "Passage"}
        </h3>
        {referenceKo && referenceEn && (
          <p className="text-body-md text-on-surface-variant">{referenceKo}</p>
        )}
      </div>

      {!entry && (
        <p className="text-body-md text-on-surface-variant">
          A passage is scheduled but no reflection has been written yet.
        </p>
      )}

      {reflectionExcerpt && (
        <div className="rounded-[1rem] p-space-sm" style={innerTranslucent}>
          <p className="text-label-sm uppercase tracking-wider text-accent-gold-deep font-semibold mb-1">
            Reflection
          </p>
          <p className="text-body-md text-neu-primary line-clamp-4 whitespace-pre-wrap">
            {reflectionExcerpt}
          </p>
        </div>
      )}
      {prayerExcerpt && (
        <div className="rounded-[1rem] p-space-sm" style={innerTranslucent}>
          <p className="text-label-sm uppercase tracking-wider text-accent-gold-deep font-semibold mb-1">
            Prayer
          </p>
          <p className="text-body-md text-neu-primary line-clamp-3 whitespace-pre-wrap">
            {prayerExcerpt}
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 text-label-sm text-on-surface-variant">
        {entry?.is_complete ? (
          <span className="inline-flex items-center gap-1 px-space-sm py-1 rounded-full text-accent-cta font-semibold" style={innerTranslucent}>
            <FiCheckCircle /> Complete
          </span>
        ) : null}
        {hasSundayPrep ? (
          <span className="inline-flex items-center gap-1 px-space-sm py-1 rounded-full text-accent-cta font-semibold" style={innerTranslucent}>
            Sunday preparation
          </span>
        ) : (
          <span className="inline-flex items-center px-space-sm py-1 rounded-full text-on-surface-variant" style={innerTranslucent}>
            Private
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-space-sm">
        <Link
          to={`/devotions/date/${passage.passage_date}`}
          className={`${primaryPill} flex-1 min-w-0 justify-center whitespace-nowrap px-3 sm:px-space-md`}
        >
          <FiBookOpen className="shrink-0" />
          <span className="truncate">
            <span className="sm:hidden">Open</span>
            <span className="hidden sm:inline">Open Reflection</span>
          </span>
        </Link>
        <Link
          to="/devotions/sunday"
          className={`${secondaryPill} flex-1 min-w-0 justify-center whitespace-nowrap px-3 sm:px-space-md`}
        >
          <span className="truncate">
            <span className="sm:hidden">Sunday</span>
            <span className="hidden sm:inline">Prepare for Sunday</span>
          </span>
        </Link>
      </div>
    </>
  );
};

export default DevotionHistoryPage;
